import { memo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import MyAccountDropdown from './MyAccountDropdown';
import Tour from './Tour';
import { Breadcrumb } from './common';
import { CLIENT_DOWNLOAD_URL } from '../constants';
import NotificationList from './notifications/NotificationList';
import QuickAdd from './QuickAdd';

import { HeaderTimer } from './timer';
import { startTour } from '../actions';
import { stopTimer, checkOut, setShowTimerWidget } from '../actions/timer';
import QuickSearchModal from './common/QuickSearchModal';

const Header = ({ pageHeading, bredCrumbLinks }) => {
  const isStartTour = useSelector((state) => state.isStartTour);
  const userDetails = useSelector((state) => state.userReducer);
  const permissions = useSelector((state) => state.permissionsReducer);
  const features = useSelector((state) => state.featuresReducer);
  const timer = useSelector((state) => state.timerReducer);
  const billingDetails = useSelector((state) => state.billingReducer);
  const sideNavbarCollapsedViewDetails = useSelector(
    (state) => state.collpasedSideNavbarToggle
  );
  const currentPlan = (
    (userDetails &&
      userDetails.subscription.currentPlan !== null &&
      userDetails.subscription.currentPlan) ||
    ''
  ).toLowerCase();
  const accountStatus =
    (billingDetails && billingDetails.accountStatus) || null;
  const isFreeTrial =
    (currentPlan === 'trial' && accountStatus !== 'cancel') || false;
  const isWebTrackingEnabled = features && features.isWebTracking;

  const dispatch = useDispatch();
  // Restart tour on start tour button click
  const restartTour = () => {
    dispatch(startTour());
  };

  // Auto-stop an active web timer and close its popover if web tracking gets disabled mid-session
  useEffect(() => {
    if (isWebTrackingEnabled === false) {
      if (timer.isRunning) {
        dispatch(stopTimer());
        dispatch(checkOut({ wsId: timer.worksessionId }));
        toast(
          'Your timer has been stopped because web tracking was disabled.',
          {
            hideProgressBar: true,
          }
        );
      }
      if (timer.showTimerWidget) {
        dispatch(setShowTimerWidget());
      }
    }
  }, [
    isWebTrackingEnabled,
    timer.isRunning,
    timer.showTimerWidget,
    timer.worksessionId,
    dispatch,
  ]);

  return (
    <header
      className={
        isFreeTrial && userDetails.freeTrialStatus
          ? `bg-primary topbar free-trial ${
              sideNavbarCollapsedViewDetails &&
              sideNavbarCollapsedViewDetails.sideNavbarCollapsedView
                ? 'collapsed-topbar'
                : ''
            }`
          : `bg-primary topbar ${
              sideNavbarCollapsedViewDetails &&
              sideNavbarCollapsedViewDetails.sideNavbarCollapsedView
                ? 'collapsed-topbar'
                : ''
            }`
      }
    >
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-12">
            <div className="header m-0">
              <div className="header-body border-0">
                <div className="row align-items-center">
                  {pageHeading && (
                    <div className="col">
                      <h6 className="header-pretitle">
                        {pageHeading.preTitle}
                      </h6>
                      <h2 className="header-title">
                        <span>{pageHeading.title} </span>
                        {bredCrumbLinks && (
                          <Breadcrumb bredCrumbLinks={bredCrumbLinks} />
                        )}
                      </h2>
                    </div>
                  )}
                  {features && features.isWebTracking && (
                    <div className="col-auto">
                      <HeaderTimer />
                    </div>
                  )}

                  <div className="col-auto">
                    {permissions && permissions.includes('quick_add') && (
                      <QuickAdd />
                    )}
                    {permissions && permissions.includes('quick_search') && (
                      <QuickSearchModal />
                    )}
                    <Link
                      className="btn text-white font-size-lg"
                      to={{ pathname: CLIENT_DOWNLOAD_URL }}
                      target="_blank"
                      role="button"
                    >
                      <Tippy content="Download HiveDesk client" theme="light">
                        <i className="mdi icon-20px mdi-download" />
                      </Tippy>
                    </Link>
                    <Link
                      className="btn text-white font-size-lg d-none d-xl-inline-block"
                      to={{ hash: '#' }}
                      role="button"
                      id="guide_start"
                      onClick={() => {
                        restartTour();
                      }}
                    >
                      <Tippy content="Start tour" theme="light">
                        {/* <i className="mdi mdi-television-guide" /> */}
                        <i className="mdi icon-20px mdi-presentation-play" />
                      </Tippy>
                    </Link>
                    {permissions &&
                      permissions.includes('configuration_manage') && (
                        <Link
                          className="btn text-white font-size-lg"
                          to="/settings/general"
                          role="button"
                        >
                          <Tippy content="Settings" theme="light">
                            <i className="mdi icon-20px mdi-settings-outline" />
                          </Tippy>
                        </Link>
                      )}
                    <NotificationList userDetails={userDetails} />
                  </div>

                  <div className="col-auto dropdown d-none d-sm-block">
                    <MyAccountDropdown />
                  </div>
                  {isStartTour && <Tour start={isStartTour} />}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

Header.propTypes = {
  pageHeading: PropTypes.object,
  bredCrumbLinks: PropTypes.array,
};

export default memo(Header);
